<template>
  <div class="editor mc-container">
    <mc-split-view
      container-class="editor__steps"
      :has-right-header="!!selected"
      height="calc(100vh - 344px)"
    >
      <template #leftHeader>
        <div class="header-title">
          Steps
        </div>
        <b-button
          outlined
          size="is-small"
          type="is-primary"
          @click="isAddingStep = true"
        >
          Add Step
        </b-button>
      </template>
      <template #leftBody>
        <mc-dnd-list
          v-if="attached.length > 0"
          container-class="editor__list"
          :list="attached"
          :on-drop="moveStepHandler"
        >
          <template #item="{ item }">
            <step-item
              attached
              :selected="selectedPath === getStepPath(item)"
              :step="item"
              @click="selectedPath = $event"
            />
          </template>
        </mc-dnd-list>
        <div v-else class="editor__info has-text-grey">
          <b-icon icon="playlist-plus" />
          <div class="content">
            There are no steps attached to this concept
          </div>
        </div>
        <div v-if="detached.length > 0" class="editor__detached">
          <div class="editor__detached-title title is-6">
            Unattached Steps
          </div>
          <div>
            <step-item
              v-for="step in detached"
              :key="step.id"
              :attached="false"
              :selected="selectedPath === getStepPath(step)"
              :step="step"
              @click="selectedPath = $event"
            />
          </div>
        </div>
      </template>
      <template v-if="selected" #rightHeader>
        <div class="header-title">
          {{ selected.collection === 'videos' ? 'Video' : 'Test' }}
        </div>
        <b-button
          :loading="tests.submitting || videos.submitting"
          size="is-small"
          type="is-primary"
          @click="submitHandler"
        >
          Update
        </b-button>
      </template>
      <template #rightBody>
        <div v-if="!selected" class="editor__info has-text-grey">
          <b-icon icon="information" />
          <div class="content">
            Select a step to edit it
          </div>
        </div>
        <component
          :is="stepComponent"
          v-else
          :id="selected.id"
          :key="selectedPath"
          ref="form"
          :on-submit="updateStepHandler"
        />
      </template>
    </mc-split-view>
    <AddStepModal
      :active="isAddingStep"
      :on-close="closeModalHandler"
      @added="selectedPath = $event"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';

import AddStepModal from './AddStepModal';
import StepItem from './StepItem';
import TestForm from './TestForm';
import VideoForm from './VideoForm';

export default {
  data: function() {
    return {
      isAddingStep: false,
      selectedPath: null,
      testForm: TestForm,
      videoForm: VideoForm
    };
  },
  computed: {
    ...mapState(['concepts', 'tests', 'videos']),
    ...mapGetters('concepts', ['attached', 'detached']),
    selected() {
      if (!this.selectedPath) return null;
      return [...this.attached, ...this.detached].find(
        o => this.getStepPath(o) === this.selectedPath
      );
    },
    stepComponent() {
      if (!this.selected) return 'div';
      else if (this.selected.collection === 'videos') return this.videoForm;
      else return this.testForm;
    }
  },
  methods: {
    ...mapActions('concepts', ['moveReference']),
    ...mapActions('tests', ['updateTest']),
    ...mapActions('videos', ['updateVideo']),
    closeModalHandler() {
      this.isAddingStep = false;
    },
    getStepPath(step) {
      return `${step.collection}/${step.id}`;
    },
    async moveStepHandler({ fromItem, toItem }) {
      const from = this.getStepPath(fromItem);
      const to = this.getStepPath(toItem);
      try {
        await this.moveReference({ from, to });
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Failed to move step!',
          type: 'is-danger'
        });
      }
    },
    submitHandler() {
      this.$refs.form.submit();
    },
    async updateStepHandler() {
      try {
        if (this.selected.collection === 'videos') {
          await this.updateVideo({ ...this.videos.selected });
        } else {
          await this.updateTest({ ...this.tests.selected });
        }
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Failed to update step!',
          type: 'is-danger'
        });
      }
    }
  },
  components: {
    AddStepModal,
    StepItem
  }
};
</script>

<style lang="scss" scoped>
.editor {
  position: relative;
  background-color: #fff;
  border: 1px solid #e2e2e2;
  border-radius: $border-radius;

  &__list {
    // Overrides default dnd styles
    & /deep/ .dnd {
      &__draggable {
        box-shadow: none;
        padding: 0;
        border-bottom: 1px solid $border-color;

        &:not(:last-child) {
          margin-bottom: 0;
        }
      }

      &__draggable-handle {
        margin-right: 0;
        margin: 0 0.5rem;
      }
    }
  }

  &__detached {
    display: flex;
    flex-direction: column;
  }

  &__detached-title {
    text-align: center;
    padding: 1rem;
    margin-bottom: 0;
  }

  &__info {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 3rem 1rem;
    text-align: center;

    .content {
      margin-left: 0.5rem;
    }
  }
}
</style>
